import React, { useCallback, useMemo, useState, useEffect } from "react";
import CustomTable, { Column } from "../../../components/CustomTable";
import {
  ArrowPathIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useNavigate, useLocation } from "react-router-dom";
import { getUserIdTokenForIonTime } from "../../../helper/api";
import { useReportTimesheetContext } from "../../../context/ReportTimsheetContex";
import { approverTimesheetResponse } from "../../../model/timesheet";
import moment from "moment";
import { useLoading } from "../../../context/LoadingContext";
import { ReportStatusEnum } from "../../../helper/constant";
import StaticModal from "../../../components/staticModel";
import useTimeSheetApi from "../../../api/timesheet";

export interface Data {
  id: number;
  projectName: string;
  timesheetPeriod: string;
  tsApprover: string;
  status: string;
}

const ApproveRejectList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const getUserId = getUserIdTokenForIonTime();
  const { isLoading } = useLoading();
  const { approverTimesheetList, getApproverTimesheetDataList } =
    useReportTimesheetContext();
  const { approveRejectReport } = useTimeSheetApi();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12

  const [projectIdvalue, setProjectId] = useState<string>("");
  const [memberIdvalue, setMemberId] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagePercount] = useState(10);

  // Selection and Bulk Approval State
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approvalProgress, setApprovalProgress] = useState({
    current: 0,
    total: 0,
  });
  const [isApproving, setIsApproving] = useState(false);

  const columns: Column<approverTimesheetResponse>[] = [
    { header: "Project Name", key: "projects", enableLink: false, path: "" },
    { header: "Member Name", key: "member", enableLink: false, path: "" },
    { header: "Status", key: "status", enableLink: false, path: "" },
    {
      header: "No. of hours clocked",
      key: "hours_clocked" as keyof approverTimesheetResponse,
      enableLink: false,
      path: "",
    },
    {
      header: "Timesheet Period",
      key: "start_date",
      enableLink: true,
      path: "/reviewApproveTimesheet",
    },
    { header: "TS Approver", key: "approvers", enableLink: false, path: "" },
  ];

  // Central fetch function. Always reads the *current* filter state unless
  // an explicit override is passed in, so callers don't have to worry about
  // stale closures.
  const getCall = useCallback(
    async (
      pageNo?: number | null,
      memberId?: number | null,
      projectID?: number | null,
      month?: number | null,
      year?: number | null,
    ) => {
      setSelectedRows([]);
      if (getUserId) {
        const payload = {
          user_id: Number(getUserId),
          status: [
            ReportStatusEnum.Waitingforapproval,
            ReportStatusEnum.PartiallyApproved,
          ],
          member_id:
            memberId !== undefined
              ? memberId
              : memberIdvalue
                ? Number(memberIdvalue)
                : null,
          project_id:
            projectID !== undefined
              ? projectID
              : projectIdvalue
                ? Number(projectIdvalue)
                : null,
          month: month ?? selectedMonth,
          year: year ?? selectedYear,
          page: pageNo ?? currentPage,
          page_per_count: pagePercount,
        };
        await getApproverTimesheetDataList(payload);
      }
    },
    [
      currentPage,
      getApproverTimesheetDataList,
      getUserId,
      memberIdvalue,
      pagePercount,
      projectIdvalue,
      selectedMonth,
      selectedYear,
    ],
  );

  // Single mount effect: restore filters (from navigation state, falling
  // back to sessionStorage) and fire exactly ONE fetch using whichever
  // filters end up being active. Previously this was split across two
  // separate effects, so the initial fetch ran with the *default* month/year
  // and the restored filters were applied to state only, never re-fetched -
  // that's what made filters look "disturbed" after navigating back.
  useEffect(() => {
    let filters: any = null;

    if (location.state?.filters) {
      filters = location.state.filters;
    } else {
      const savedFilters = sessionStorage.getItem("timesheetFilters");
      if (savedFilters) {
        try {
          filters = JSON.parse(savedFilters);
        } catch (e) {
          filters = null;
        }
      }
    }

    if (filters) {
      const restoredProject = filters.projectIdvalue ?? "";
      const restoredMember = filters.memberIdvalue ?? "";
      // const restoredMonth = filters.selectedMonth ?? currentMonth;
      // const restoredYear = filters.selectedYear ?? currentYear;
      const restoredMonth =
        filters.selectedMonth !== undefined
          ? filters.selectedMonth
          : currentMonth;
      const restoredYear =
        filters.selectedYear !== undefined ? filters.selectedYear : currentYear;
      const restoredPage = filters.currentPage ?? 1;

      setProjectId(restoredProject);
      setMemberId(restoredMember);
      setSelectedMonth(restoredMonth);
      setSelectedYear(restoredYear);
      setCurrentPage(restoredPage);
      sessionStorage.setItem("timesheetFilters", JSON.stringify(filters));

      getCall(
        restoredPage,
        restoredMember ? Number(restoredMember) : null,
        restoredProject ? Number(restoredProject) : null,
        restoredMonth,
        restoredYear,
      );
    } else {
      getCall(1, null, null, currentMonth, currentYear);
    }
  }, []);

  const monthsList = [
    { id: 0, name: "" },
    { id: 1, name: "January" },
    { id: 2, name: "February" },
    { id: 3, name: "March" },
    { id: 4, name: "April" },
    { id: 5, name: "May" },
    { id: 6, name: "June" },
    { id: 7, name: "July" },
    { id: 8, name: "August" },
    { id: 9, name: "September" },
    { id: 10, name: "October" },
    { id: 11, name: "November" },
    { id: 12, name: "December" },
  ];

  const yearsList = [
    currentYear - 2,
    currentYear - 1,
    currentYear,
    currentYear + 1,
  ];

  const mytimesheetList = useMemo(() => {
    if (!approverTimesheetList) return [];

    return approverTimesheetList.data.map((item) => {
      const hours = Number((item as any).hours_clocked ?? 0);
      const required = Number((item as any).required_hours ?? 40);

      // Extract date arrays from backend response
      const holidays = (item as any).holidays ?? [];
      const leaves = (item as any).leaves ?? [];
      const weekends = (item as any).weekends ?? [];
      const rejectedDates = (item as any).rejected_dates ?? [];

      // Check if there's any legitimate reason for low hours
      const hasExcuse =
        holidays.length > 0 || leaves.length > 0 || weekends.length > 0;

      // Only mark RED if hours clocked are strictly less than required and less than 40
      // AND there's no legitimate excuse (holiday, leave, or weekend)
      const isBelowTarget = hours < required && hours < 40 && !hasExcuse;

      return {
        ...item,
        original_start_date: item.start_date,
        start_date:
          moment(item["start_date"]).format("D MMM YYYY") +
          " - " +
          moment(item.end_date).format("D MMM YYYY"),
        approvers: item.approvers.join(", "),
        member: item["member-name"],
        projects: item.projects.join(", "),
        hours_clocked: (
          <div className="flex flex-col gap-1">
            <span
              className={
                isBelowTarget
                  ? "text-red-600 font-bold dark:text-red-400"
                  : "text-gray-900 dark:text-white"
              }
            >
              {hours} hrs
            </span>
            {holidays.length > 0 && (
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                Public Holiday ({holidays.length})
              </span>
            )}
            {leaves.length > 0 && (
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                Leave ({leaves.length})
              </span>
            )}
            {weekends.length > 0 && (
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                Weekend ({weekends.length})
              </span>
            )}
            {rejectedDates.length > 0 && (
              <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                Rejected ({rejectedDates.length})
              </span>
            )}
          </div>
        ),
      };
    });
  }, [approverTimesheetList]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const handleNavigation = (path: string | undefined, states: number) => {
    const filters = {
      projectIdvalue,
      memberIdvalue,
      selectedMonth,
      selectedYear,
      currentPage,
    };
    sessionStorage.setItem("timesheetFilters", JSON.stringify(filters));

    const data = mytimesheetList.filter((item) => item.reports_id === states);
    if (data.length > 0) {
      navigate(path ?? "", {
        state: {
          id: data[0]["start_date"],
          memberID: data[0]["member-id"],
          employeeName: data[0]["member-name"],
          role: "manager",
          employeeNameData: data[0]["member-name"],
          filters,
        },
      });
    }
  };

  const refreshTable = () => {
    // getCall(1, null, null);
    setMemberId("");
    setProjectId("");
    setSelectedMonth(currentMonth);
    setSelectedYear(currentYear);
    setCurrentPage(1);
    // Clear sessionStorage filters when explicitly refreshing
    sessionStorage.removeItem("timesheetFilters");
    getCall(1, null, null, currentMonth, currentYear);
  };

  // Applies whatever filters are currently selected in the dropdowns.
  // Filter dropdowns only update local state on change - they no longer
  // fire a request per keystroke/selection, which was what made the page
  // "reload" and disturb the other filters. The user now picks
  // project/member/month/year and explicitly applies them here.
  const applyFilters = () => {
    setCurrentPage(1);
    getCall(
      1,
      memberIdvalue ? Number(memberIdvalue) : null,
      projectIdvalue ? Number(projectIdvalue) : null,
      selectedMonth,
      selectedYear,
    );
  };

  const totalPages = Math.ceil(
    Number(approverTimesheetList?.record_total_count) / pagePercount,
  );

  const startRecord = (currentPage - 1) * pagePercount + 1;
  const endRecord = Math.min(
    startRecord + pagePercount - 1,
    Number(approverTimesheetList?.record_total_count),
  );

  const handlePrePage = () => {
    if (currentPage > 1) {
      const prev = currentPage - 1;
      setCurrentPage(prev);
      getCall(prev);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const next = currentPage + 1;
      setCurrentPage(next);
      getCall(next);
    }
  };

  // Bulk Approval Logic
  const handleSelectionChange = (ids: number[]) => {
    setSelectedRows(ids);
  };

  const getDatesBetween = (startDate: string, endDate: string) => {
    const dates = [];
    const currDate = moment(startDate);
    const lastDate = moment(endDate);

    while (currDate.diff(lastDate) <= 0) {
      dates.push(currDate.date());
      currDate.add(1, "days");
    }
    return dates;
  };

  const handleBulkApprove = async () => {
    setIsApproving(true);
    setApprovalProgress({ current: 0, total: selectedRows.length });

    for (let i = 0; i < selectedRows.length; i++) {
      const reportId = selectedRows[i];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const reportData = mytimesheetList.find(
        (r) => r.reports_id === reportId,
      ) as any;

      if (reportData) {
        const days = getDatesBetween(
          reportData.original_start_date,
          reportData.end_date,
        );
        const payload = {
          status: ReportStatusEnum.Approved,
          user_id: Number(getUserId),
          report_id: reportId,
          remarks: "Approved",
          dates: days,
        };

        try {
          await approveRejectReport(payload);
        } catch (error) {
          console.error("Error approving report", reportId, error);
        }
      }
      setApprovalProgress((prev) => ({ ...prev, current: i + 1 }));
    }

    sessionStorage.removeItem("timesheetFilters");
    setIsApproving(false);
    setIsApproveModalOpen(false);
    // Re-apply whatever filters are currently active instead of resetting
    // them, so approving doesn't wipe out the filter selection.
    applyFilters();
    setSelectedRows([]);
  };

  return (
    <>
      <CustomTable
        idKeyName="reports_id"
        showFilter={true}
        columns={columns}
        data={mytimesheetList}
        handleNavigation={handleNavigation}
        showCheckbox={true}
        selectedIds={selectedRows}
        onSelectionChange={handleSelectionChange}
      >
        <div>
          <div className="max-w-6xl gap-x-4 gap-y-4 grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-6">
            {/* Project Filter */}
            <div>
              <label
                htmlFor="project"
                className="block text-sm font-medium leading-6 text-gray-900 dark:text-white"
              >
                Project
              </label>
              <div className="mt-2">
                <select
                  id="project"
                  value={projectIdvalue}
                  onChange={(e) => {
                    // setCurrentPage(1);
                    // const newProjectId = e.target.value
                    //   ? Number(e.target.value)
                    //   : null;
                    // getCall(1, null, newProjectId); // drop stale member filter
                    setProjectId(e.target.value);
                    setMemberId(""); // clear member selection/UI too
                  }}
                  className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                >
                  <option value="">All Projects</option>
                  {approverTimesheetList?.options.projects.map((item) => (
                    <option key={item["project-id"]} value={item["project-id"]}>
                      {item["project-name"]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Member Filter */}
            <div>
              <label
                htmlFor="member"
                className="block text-sm font-medium leading-6 text-gray-900 dark:text-white"
              >
                Member
              </label>
              <div className="mt-2">
                <select
                  id="member"
                  value={memberIdvalue}
                  onChange={(e) => {
                    // setCurrentPage(1);
                    // getCall(
                    //   1,
                    //   e.target.value ? Number(e.target.value) : null,
                    //   projectIdvalue ? Number(projectIdvalue) : null,
                    // );
                    setMemberId(e.target.value);
                  }}
                  className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                >
                  <option value="">All Members</option>
                  {approverTimesheetList?.options.members.map((item) => (
                    <option key={item["member-id"]} value={item["member-id"]}>
                      {item["member-name"]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Month Filter */}
            <div>
              <label
                htmlFor="month"
                className="block text-sm font-medium leading-6 text-gray-900 dark:text-white"
              >
                Month
              </label>
              <div className="mt-2">
                <select
                  id="month"
                  value={selectedMonth || ""}
                  onChange={(e) => {
                    const m = e.target.value ? Number(e.target.value) : 0;
                    setSelectedMonth(m);
                  }}
                  className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Months</option>
                  {monthsList.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name || "All Months"}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Year Filter */}
            <div>
              <label
                htmlFor="year"
                className="block text-sm font-medium leading-6 text-gray-900 dark:text-white"
              >
                Year
              </label>
              <div className="mt-2">
                <select
                  id="year"
                  value={selectedYear}
                  onChange={(e) => {
                    const y = Number(e.target.value);
                    setSelectedYear(y);
                  }}
                  className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                >
                  {yearsList.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Apply Filters Button */}
            <div className="flex items-end">
              <button
                type="button"
                onClick={applyFilters}
                title="Apply Filters"
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm w-full bg-blue-700 hover:bg-blue-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <MagnifyingGlassIcon className="h-5 w-5 text-white" />
                Apply
              </button>
            </div>

            {/* Refresh Button */}
            <div className="flex items-end">
              <button
                type="button"
                onClick={refreshTable}
                title="Reset Filters"
                className="flex items-center px-4 py-2 mt-9 text-sm bg-red-700 hover:bg-red-800 text-white rounded-lg focus:outline-none"
              >
                <ArrowPathIcon className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          {selectedRows.length > 0 && (
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setApprovalProgress({
                    current: 0,
                    total: selectedRows.length,
                  });
                  setIsApproveModalOpen(true);
                }}
                className="flex items-center px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg focus:outline-none"
              >
                Approve Selected ({selectedRows.length})
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-gray-700 dark:text-gray-400">
              Showing{" "}
              <span className="font-semibold text-gray-900 dark:text-white">
                {Number(approverTimesheetList?.record_total_count) > 0
                  ? startRecord
                  : 0}
              </span>{" "}
              to{" "}
              <span className="font-semibold text-gray-900 dark:text-white">
                {endRecord}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-900 dark:text-white">
                {approverTimesheetList?.record_total_count ?? 0}
              </span>{" "}
              Records
            </span>
            {Number(approverTimesheetList?.record_total_count) > 10 && (
              <div className="inline-flex xs:mt-0">
                <button
                  disabled={currentPage === 1}
                  onClick={handlePrePage}
                  className="flex items-center justify-center px-3 h-8 text-sm font-medium text-white bg-gray-800 rounded-s hover:bg-gray-900 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-50"
                >
                  <ArrowLeftIcon className="h-4 w-4 text-white mr-2" />
                  Prev
                </button>
                <select
                  id="page"
                  value={currentPage}
                  onChange={(e) => {
                    const p = Number(e.target.value);
                    setCurrentPage(p);
                    getCall(p);
                  }}
                  className="shadow-sm bg-white border border-gray-800 dark:text-gray-400 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 block px-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-700 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                >
                  {Array.from({ length: totalPages }, (_, index) => (
                    <option key={index + 1} value={index + 1}>
                      {index + 1}
                    </option>
                  ))}
                </select>
                <button
                  disabled={currentPage === totalPages}
                  onClick={handleNextPage}
                  className="flex items-center justify-center px-3 h-8 text-sm font-medium text-white bg-gray-800 border-0 border-s border-gray-700 rounded-e hover:bg-gray-900 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-50"
                >
                  Next
                  <ArrowRightIcon className="h-4 w-4 text-white ml-2" />
                </button>
              </div>
            )}
          </div>
        </div>
      </CustomTable>

      <StaticModal
        isOpen={isApproveModalOpen}
        onClose={() => !isApproving && setIsApproveModalOpen(false)}
        headerName="Approve Timesheets"
      >
        <div className="p-4">
          {!isApproving ? (
            <>
              <p className="mb-4 text-gray-700 dark:text-gray-300">
                Are you sure you want to approve {selectedRows.length} selected
                timesheets?
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setIsApproveModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkApprove}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Confirm
                </button>
              </div>
            </>
          ) : (
            <div className="text-center">
              <p className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">
                Approving...
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 mb-2">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{
                    width: `${(approvalProgress.current / approvalProgress.total) * 100}%`,
                  }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {approvalProgress.current} / {approvalProgress.total} processed
              </p>
            </div>
          )}
        </div>
      </StaticModal>
    </>
  );
};

export default ApproveRejectList;
